import {
  CFormCheck,
  CFormInput,
  CModal,
  CModalBody,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { createRef, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import TranslateIcon from "../../components/icons/TranslateIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { iconBtnStyle, labelStyle, tdStyle, thStyle } from "../../helpers/constants";

/* ─────────────── Edit modal ─────────────── */

interface newCategoryModalProps {
  open: boolean;
  onClose: () => void;
  label?: string;
  order?: number;
  image?: File | string;
  id?: string;
}
interface updateCategoryProp {
  label: string;
  order: number;
  picture?: File;
}

const CategoryModal = ({
  open,
  onClose,
  label = "",
  order = 0,
  image = "",
  id = "",
}: newCategoryModalProps) => {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const [categoryName, setCategoryName] = useState(label);
  const [categoryOrder, setCategoryOrder] = useState(order);
  const queryClient = useQueryClient();
  const pictureRef = createRef<HTMLInputElement>();

  const updateCategory = useMutation((data: updateCategoryProp) => {
    return apiService.MakePutRequest(`categories/${id}`, data, token, true);
  });

  const updateCategoryHandler = () => {
    updateCategory.mutate(
      {
        label: categoryName,
        order: categoryOrder,
        picture: pictureRef?.current?.files?.[0]! || " ",
      },

      {
        onError() {
          toastError(general.fr.message.operationFailed);
        },
        onSuccess() {
          queryClient.invalidateQueries(["getAllCategories"]);
          toastSuccess(general.fr.message.categoryUpdated);
          onClose();
          setCategoryName("");
        },
      },
    );
  };

  return (
    <CModal visible={open} onClose={onClose} alignment="center">
      <CModalBody className="p-4">
        <h5 className="fw-bold mb-4">Mise à jour de la catégorie</h5>

        <div className="mb-3">
          <label htmlFor="label" style={labelStyle}>
            Nom de la catégorie
          </label>
          <input
            id="label"
            type="text"
            className="custom-input w-100 mt-1"
            name="label"
            placeholder="Nom de la catégorie"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="order" style={labelStyle}>
            Ordre de la catégorie
          </label>
          <input
            id="order"
            type="number"
            className="custom-input w-100 mt-1"
            name="order"
            placeholder="Ordre de la catégorie"
            value={categoryOrder}
            onChange={(e) => setCategoryOrder(parseInt(e.target.value))}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="formFile" style={labelStyle}>
            Image de catégorie
          </label>
          <div className="d-flex align-items-center gap-3 mt-1">
            {image && (
              <img
                src={image as string}
                alt=""
                width={64}
                height={64}
                className="flex-shrink-0"
                style={{ borderRadius: 12, objectFit: "cover" }}
              />
            )}
            <CFormInput type="file" id="formFile" ref={pictureRef} />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn rounded-pill border px-4"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={updateCategoryHandler}
            disabled={updateCategory.isLoading}
            className="btn btn-primary rounded-pill text-white px-4 fw-bold d-flex align-items-center gap-2"
          >
            {updateCategory.isLoading ? (
              <>
                <CSpinner size="sm" /> {general.fr.wait}
              </>
            ) : (
              "Sauvegarder"
            )}
          </button>
        </div>
      </CModalBody>
    </CModal>
  );
};

/* ─────────────── Row ─────────────── */

function TableBodyRowContent({
  category,
  deletedList,
}: {
  category: Category;
  deletedList: React.Dispatch<BulkDeleteActions>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { isAdmin } = useAuth();

  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();

  const queryClient = useQueryClient();

  const deleteCategory = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`categories/${id}`, token);
  });

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id: category.id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id: category.id });
    }
  }, [deletedList, isChecked, category.id]);

  const removeCategory = () => {
    deleteCategory.mutate(category.id, {
      onError(error) {
        console.log(error);
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.categoryDeleted);
        queryClient.invalidateQueries("getAllCategories");
      },
    });
  };

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        <CFormCheck
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </CTableDataCell>

      {/* Image + name */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 200 }}
        >
          <img
            src={
              category?.image ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                category?.label ?? "",
              )}`
            }
            alt=""
            width={44}
            height={44}
            className="flex-shrink-0"
            style={{ borderRadius: 10, objectFit: "cover" }}
          />
          <span className="fw-semibold text-capitalize">{category.label}</span>
        </div>
      </CTableDataCell>

      {/* Order */}
      <CTableDataCell style={tdStyle}>
        {category.order != null ? (
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-pill fw-bold"
            style={{
              minWidth: 28,
              height: 24,
              padding: "0 8px",
              fontSize: 12,
              backgroundColor: "#f2f2f2",
              color: "#555",
            }}
          >
            {category.order}
          </span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <button
            type="button"
            title="Modifier"
            aria-label="Modifier"
            onClick={() => setShowEditModal(true)}
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={iconBtnStyle}
          >
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </button>

          {isAdmin && (
            <Link
              to="/category-translations/new"
              state={{ category }}
              title="Traduire"
              aria-label="Traduire"
              className="d-inline-flex align-items-center justify-content-center rounded-circle"
              style={iconBtnStyle}
            >
              <TranslateIcon width="18" height="18" fill="#000" />
            </Link>
          )}

          <button
            type="button"
            title="Supprimer"
            aria-label="Supprimer"
            onClick={() => setShowModal(true)}
            disabled={deleteCategory.isLoading}
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={{ ...iconBtnStyle, backgroundColor: "rgba(220,53,69,0.10)" }}
          >
            {deleteCategory.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </button>
        </div>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={removeCategory}
        onClose={() => setShowModal(false)}
      />
      <CategoryModal
        onClose={() => setShowEditModal(false)}
        open={showEditModal}
        id={category?.id}
        label={category?.label}
        image={category?.image}
        order={category?.order}
      />
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

interface TableProps {
  categories: Category[];
  categoriesToBeDeletedList: React.Dispatch<BulkDeleteActions>;
}

export default function CategoriesTable({
  categories,
  categoriesToBeDeletedList,
}: TableProps) {
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }} />
            <CTableHeaderCell scope="col" style={thStyle}>
              Nom de la catégorie
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Ordre
            </CTableHeaderCell>
            <CTableHeaderCell
              scope="col"
              style={{ ...thStyle, textAlign: "right" }}
            >
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {categories?.map((category) => (
            <TableBodyRowContent
              category={category}
              key={category?.id}
              deletedList={categoriesToBeDeletedList}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
